import { Injectable } from '@angular/core';
import { FileColumnDataType } from '@app/features/admin/enum/import-assets';

@Injectable()
export class ValuesParserService {
    private readonly convertFunctions;

    constructor() {
        this.convertFunctions = {
            [FileColumnDataType.STRING]: (valueToConvert: string) =>
                this.normalizeTextValue(valueToConvert),
            [FileColumnDataType.NUMBER]: (valueToConvert: string) =>
                this.parseNumberValue(valueToConvert),
            [FileColumnDataType.BOOLEAN]: (valueToConvert: string) =>
                this.parseBooleanValue(valueToConvert),
            [FileColumnDataType.NUMBER_ARRAY]: (valueToConvert: string) =>
                this.parseArrayNumberValue(valueToConvert),
            [FileColumnDataType.STRING_ARRAY]: (valueToConvert: string) =>
                this.parseArrayStringValue(valueToConvert),
        };
    }

    parseValue(type: FileColumnDataType, value: string): unknown {
        try {
            return this.convertFunctions[type](value);
        } catch {
            throw new Error(`value must be a ${this.getColumnDataTypeString(type)}.`);
        }
    }

    private getColumnDataTypeString(type: FileColumnDataType): string {
        const typeStrings = {
            [FileColumnDataType.STRING]: 'text',
            [FileColumnDataType.NUMBER]: 'number',
            [FileColumnDataType.BOOLEAN]: 'true/false',
            [FileColumnDataType.NUMBER_ARRAY]: 'semi-colon separated list of numbers',
            [FileColumnDataType.STRING_ARRAY]: 'semi-colon separated list of texts',
        };

        return typeStrings[type];
    }

    private normalizeTextValue(value: string): string {
        if (!value) return value;
        return value
            .normalize('NFKC')
            .replace(/[\u2010-\u2015]/g, '-')
            .replace(/[‘’‚‛]/g, "'")
            .replace(/[“”„‟]/g, '"')
            .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
            .trim();
    }

    private parseNumberValue(value: string): number {
        if (isNaN(+value)) {
            throw new Error();
        }

        return Number(value);
    }

    private parseArrayNumberValue(value: string): number[] {
        const array: number[] = [];

        value
            .split(';')
            .map((item) => item.trim())
            .filter((item) => !!item)
            .forEach((item) => {
                array.push(this.parseNumberValue(item));
            });

        return array;
    }

    private parseArrayStringValue(value: string): string[] {
        return value
            .split(';')
            .map((item) => this.normalizeTextValue(item).trim())
            .filter((item) => !!item);
    }

    private parseBooleanValue(value: string): boolean {
        return value === 'true';
    }
}
